"use client";

import { useContentfulInspectorMode } from "@scott/contentful/live-preview-init";
import { useState } from "react";
import { Image, type ImageProps } from "../../elements/image/image";
import {
  type D2CBackgroundColor,
  type D2CTextColor,
  deriveD2CTextColors,
} from "../../lib/d2c-colors";
import { cn } from "../../lib/utils";
import { Modal } from "../modal/modal";
import {
  getRichTextPlainText,
  TextSection,
  truncateRichTextDocument,
} from "../text-section/text-section";
import { TextSectionPreview } from "../text-section/text-section.preview";
import type { ImageArrangementProps } from "./image-arrangement-types";

type ImageArrangementItem = ImageArrangementProps["images"][number];

const TEXT_TRUNCATE_AT = 175;
const DEFAULT_ASPECT_RATIO = "3:4";

// next/image rejects a width without a height, so the dialog image derives one
// from the item's aspect ratio instead of assuming a fixed shape.
const MODAL_IMAGE_WIDTH = 160;

export function getHeightForWidth(
  aspectRatio: ImageProps["aspectRatio"],
  width: number
): number {
  const [ratioWidth, ratioHeight] = aspectRatio.split(":").map(Number);
  return Math.round((width * ratioHeight) / ratioWidth);
}

interface ArrangementImageProps {
  image: ImageArrangementItem;
  background?: ImageArrangementProps["background"];
  className?: string;
  defaultAspectRatio?: ImageProps["aspectRatio"];
  readMoreLabel?: ImageArrangementProps["readMoreLabel"];
  readMoreModalTitle?: ImageArrangementProps["readMoreModalTitle"];
  sizes?: string;
}

interface ArrangementImageContentProps {
  image: ImageArrangementItem;
  backgroundColor: D2CBackgroundColor;
  textColor: D2CTextColor;
  isPreview?: boolean;
  readMoreLabel?: string;
  readMoreModalTitle?: string;
}

function ArrangementImageContent(
  props: Readonly<ArrangementImageContentProps>
) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const textColorClass: Record<typeof props.textColor, string> = {
    black: "text-black",
    white: "text-white",
    alabastergrey: "text-alabastergrey",
  };

  const shouldTruncate =
    getRichTextPlainText(props.image.text?.text?.json).length >
    TEXT_TRUNCATE_AT;

  let textContent = props.image.text;
  if (shouldTruncate) {
    textContent = props.image.text
      ? {
          ...props.image.text,
          text: {
            json: truncateRichTextDocument(
              props.image.text.text?.json,
              TEXT_TRUNCATE_AT
            ),
          },
        }
      : undefined;
  }

  const modalAspectRatio = props.image.aspectRatio || DEFAULT_ASPECT_RATIO;
  const TextComponent = props.isPreview ? TextSectionPreview : TextSection;

  return (
    <>
      <TextComponent
        backgroundColor={props.backgroundColor}
        color={props.textColor}
        columnWidth={12}
        headline={props.image.headline}
        headlineColor={props.textColor}
        headlineVariant="h5"
        outerContainerOnly={true}
        paddingBottom="none"
        paddingTop="small"
        sys={props.image.textEntrySys}
        text={textContent}
        textColumns={1}
        textVariant="p-small"
      />

      {props.readMoreLabel && shouldTruncate ? (
        <>
          <button
            aria-haspopup="dialog"
            className={cn(
              "mt-1 inline cursor-pointer bg-transparent p-0 text-left font-brand text-sm underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              textColorClass[props.textColor]
            )}
            onClick={() => setIsModalOpen(true)}
            type="button"
          >
            {props.readMoreLabel}
          </button>

          <Modal
            contentClassName="min-h-0"
            headerCentered={false}
            isOpen={isModalOpen}
            modalClassName="max-w-2xl p-6 md:max-w-3xl"
            onClose={() => setIsModalOpen(false)}
            preventAutoFocus
            title={props.readMoreModalTitle ?? ""}
            titleAs="h5"
            titleClassName="text-base font-semibold leading-8 tracking-normal"
          >
            <div className="flex flex-row gap-6">
              <div className="w-40 shrink-0">
                <Image
                  alt={props.image.alt}
                  aspectRatio={modalAspectRatio}
                  className="h-auto w-full"
                  height={getHeightForWidth(
                    modalAspectRatio,
                    MODAL_IMAGE_WIDTH
                  )}
                  loading="eager"
                  objectPosition={props.image.objectPosition}
                  src={props.image.src}
                  srcSet={props.image.srcSet}
                  width={MODAL_IMAGE_WIDTH}
                />
              </div>
              <TextSection
                color="black"
                columnWidth={12}
                headline={props.image.headline}
                headlineColor="black"
                headlineDash={false}
                headlineVariant="h5"
                outerContainerOnly={true}
                paddingBottom="none"
                paddingTop="none"
                text={props.image.text}
                textColumns={1}
                textVariant="p-small"
              />
            </div>
          </Modal>
        </>
      ) : null}
    </>
  );
}

export function ArrangementImage(props: Readonly<ArrangementImageProps>) {
  const inspector = useContentfulInspectorMode({
    entryId: props.image.isPreview ? props.image.sys?.id : undefined,
  });

  const hasTextSectionContent = Boolean(
    props.image.headline || props.image.text
  );
  const backgroundColor = props.background ?? "white";
  const { textColor } = deriveD2CTextColors(backgroundColor);
  const imageInspectorProps = inspector({ fieldId: "image" }) ?? undefined;

  const image = (
    <Image
      alt={props.image.alt}
      aspectRatio={props.image.aspectRatio || props.defaultAspectRatio || "3:4"}
      className={cn("h-auto w-full", props.className)}
      height={533}
      objectPosition={props.image.objectPosition}
      sizes={props.sizes}
      src={props.image.src}
      srcSet={props.image.srcSet}
      width={400}
    />
  );

  return (
    <>
      {props.image.isPreview ? (
        <div {...imageInspectorProps}>{image}</div>
      ) : (
        image
      )}

      {hasTextSectionContent ? (
        <ArrangementImageContent
          backgroundColor={backgroundColor}
          image={props.image}
          isPreview={props.image.isPreview}
          readMoreLabel={props.readMoreLabel}
          readMoreModalTitle={props.readMoreModalTitle}
          textColor={textColor}
        />
      ) : null}
    </>
  );
}
